/** Measurements — list view (server-side pagination/sort via the shared DataTable). */
import { useGo } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import type { Measurement } from "@aos/shared";
import type { HttpError } from "@refinedev/core";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { RefLink } from "@/components/inspector";
import { formatDateTime } from "@/lib/format";

import { DataTable, type RefineTableCore } from "../projects/_shared";

/** MeasurementList (D6) — generic CRUD list for metric measurements. */
export function MeasurementList() {
  const go = useGo();

  const columns = useMemo<ColumnDef<Measurement, unknown>[]>(
    () => [
      {
        id: "metric_id",
        accessorKey: "metric_id",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="metrics" id={row.original.metric_id} />
          </span>
        ),
      },
      {
        id: "value",
        accessorKey: "value",
        header: "Value",
        cell: ({ row }) => (
          <span className="font-mono font-medium tabular-nums">
            {row.original.value}
          </span>
        ),
      },
      {
        id: "measured_at",
        accessorKey: "measured_at",
        header: "Measured at",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.measured_at)}
          </span>
        ),
      },
      {
        id: "source_system",
        accessorKey: "source_system",
        header: "Source",
        cell: ({ row }) => row.original.source_system ?? "—",
      },
    ],
    [],
  );

  const table = useTable<Measurement, HttpError>({
    columns,
    refineCoreProps: {
      resource: "measurements",
      sorters: { initial: [{ field: "measured_at", order: "desc" }] },
      pagination: { pageSize: 25 },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Measurements"
        subtitle="Recorded metric readings over time, each tied to a metric and source."
        icon="okrs"
        actions={
          <Button
            size="sm"
            onClick={() =>
              go({ to: { resource: "measurements", action: "create" } })
            }
          >
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            measurement
          </Button>
        }
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No measurements yet."
        onRowClick={(m) =>
          go({ to: { resource: "measurements", action: "show", id: m.id } })
        }
      />
    </div>
  );
}

export default MeasurementList;
